import React, { useEffect, useState } from "react";
import { api, rupiah, formatApiError } from "@/lib/api";
import { PageHeader, Empty, StatCard, StatusBadge } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PiggyBank, Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { ReceiptDialog } from "@/components/ReceiptDialog";

export default function KeuTabungan() {
  const [students, setStudents] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const load = () => {
    api.get("/admin/savings").then((r) => setStudents(r.data)).catch(() => {});
    api.get("/admin/withdrawals").then((r) => setWithdrawals(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openDeposit = (s) => { setTarget(s); setAmount(""); setNote(""); };

  const submitDeposit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Jumlah tidak valid");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/students/${target.id}/savings`, { amount: amt, note });
      toast.success(`Tabungan ${target.name} bertambah ${rupiah(amt)}`);
      setReceipt({ tx_id: data.tx_id, title: "Setoran Tunai Berhasil", student: target.name,
        jenis: "Setoran Tabungan", amount: amt, balance_after: data.savings_balance });
      setTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/withdrawals/${id}/approve`);
      toast.success("Penarikan tabungan disetujui");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filtered = students.filter((s) =>
    s.name.toLowerCase().includes(query.toLowerCase()) || (s.nisn || "").includes(query));
  const total = students.reduce((a, s) => a + (s.savings_balance || 0), 0);
  const pending = withdrawals.filter((w) => w.status === "pending");
  const pg = usePaged(filtered, 10);

  return (
    <div>
      <PageHeader title="Tabungan Santri" subtitle="Saldo tabungan santri. Admin bisa setor tunai; penarikan diajukan orang tua & disetujui admin." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={PiggyBank} label="Total Tabungan" value={rupiah(total)} accent="amber" />
        <StatCard icon={PiggyBank} label="Jumlah Santri" value={students.length} />
        <StatCard icon={PiggyBank} label="Penarikan Menunggu" value={pending.length} accent="rose" />
      </div>

      {pending.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-2xl shadow-sm overflow-hidden mb-6">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 font-heading font-semibold text-amber-800">
            Permintaan Penarikan Tabungan
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Santri</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Catatan</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((w) => (
                <TableRow key={w.id} data-testid={`withdrawal-row-${w.id}`}>
                  <TableCell className="font-medium text-stone-800">{w.student_name}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(w.amount)}</TableCell>
                  <TableCell className="text-sm text-stone-500">{w.note || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => approve(w.id)} data-testid={`approve-${w.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-lg">Setujui</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="tabungan-search" placeholder="Cari santri / NISN…" className="pl-10 rounded-xl py-6 bg-white" />
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {filtered.length === 0 ? (
          <Empty><PiggyBank className="mx-auto mb-2" /> Tidak ada data.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nama</TableHead>
                <TableHead>NISN</TableHead>
                <TableHead>Angkatan</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Saldo Tabungan</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((s) => (
                <TableRow key={s.id} data-testid={`tabungan-row-${s.id}`}>
                  <TableCell className="font-medium text-stone-800">{s.name}</TableCell>
                  <TableCell className="font-mono text-sm">{s.nisn}</TableCell>
                  <TableCell>{s.angkatan}</TableCell>
                  <TableCell><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.gender === "putra" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{s.gender}</span></TableCell>
                  <TableCell className="text-right font-mono font-semibold text-amber-700">{rupiah(s.savings_balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => openDeposit(s)} data-testid={`deposit-tabungan-${s.id}`} className="bg-amber-600 hover:bg-amber-700 rounded-lg">
                      <Plus size={14} className="mr-1" /> Setor Tunai
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Setor Tabungan Tunai — {target?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-xl bg-stone-50 border p-3 text-sm text-stone-600">
              Tabungan saat ini: <b className="text-amber-700" data-testid="tabungan-current-balance">{rupiah(target?.savings_balance || 0)}</b>
            </div>
            <div className="space-y-2">
              <Label>Jumlah Setoran (Rp)</Label>
              <Input type="number" inputMode="numeric" data-testid="tabungan-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl" placeholder="0" />
            </div>
            <div className="space-y-2">
              <Label>Catatan (opsional)</Label>
              <Input data-testid="tabungan-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl" placeholder="cth: setor tunai di kantor" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitDeposit} disabled={saving} data-testid="confirm-tabungan-button" className="bg-amber-600 hover:bg-amber-700 rounded-xl">
              {saving ? "Menyimpan…" : "Setor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReceiptDialog receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
